import React, { useMemo } from "react"

import { ResetBenefitStatus } from "@common-shared/config/coverage.config"
import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { CoveragePatientType } from "@common-shared/config/oneVerify/oneVerifyCoverage.config"
import { PolicyColumnKey } from "@common-shared/config/oneVerify/oneVerifyPoliciesColumns.config"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { COVERAGE_CONFIG } from "../../../config/coverage.config"
import { POLICIES_CONFIG } from "../../../config/policies.config"
import { POLICIES_COLUMNS_CONFIG } from "../../../config/policiesColumns.config"
import { CollapseElement } from "../../../elements/collapse.element"
import { CoverageBreakdownBadgeElement } from "../../../elements/coverageBadges/coverageBreakdownBadge.element"
import { CoverageHistoryHelper } from "../../../helpers/coverageHistory.helper"
import { ExportReportHelper } from "../../../helpers/exportReport.helper"
import { HtmlHelper } from "../../../helpers/html.helper"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { AgenticCheckStatusComponent } from "../../policies/agentic/agenticCheckStatus.component"

export const CoverageResultGeneralComponent: React.FunctionComponent<
    ICoverageResultGeneralComponentProps
> = props => {
    const isPlanYearResetsEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_PLAN_YEAR_RESETS,
    )

    const patientType = useMemo<CoveragePatientType | null>(() => {
        return (
            props.coverageCheckHistory?.coverageResult?.customerPatientType ||
            null
        )
    }, [props.coverageCheckHistory])

    const patientId = useMemo<string | null>(() => {
        return props.coverageCheckHistory
            ? (ExportReportHelper.getMappedColumnValue(
                  props.coverageCheckHistory,
                  POLICIES_COLUMNS_CONFIG.columnsConfig[
                      PolicyColumnKey.PATIENT_ID
                  ],
                  undefined,
                  false,
              ) as string | null)
            : null
    }, [props.coverageCheckHistory])

    const patientNextAppointmentDate = useMemo<string | null>(() => {
        return props.coverageCheckHistory
            ? (ExportReportHelper.getMappedColumnValue(
                  props.coverageCheckHistory,
                  POLICIES_COLUMNS_CONFIG.columnsConfig[
                      PolicyColumnKey.PATIENT_NEXT_APPOINTMENT_DATE
                  ],
                  undefined,
                  false,
              ) as string | null)
            : null
    }, [props.coverageCheckHistory])

    const demographicsContent = useMemo<JSX.Element>(() => {
        const patientDemographicItems = props.coverageCheckHistory
            ? CoverageHistoryHelper.getPatientDemographicBreakdownItems(
                  props.coverageCheckHistory,
              )
            : COVERAGE_CONFIG.defaultDemographicBreakdownItems

        const subscriberDemographicItems = props.coverageCheckHistory
            ?.coverageResult.subscriberDemographics
            ? CoverageHistoryHelper.getSubscriberDemographicBreakdownItems(
                  props.coverageCheckHistory,
              )
            : null

        return (
            <div className="mt-[8px] flex items-start md:block">
                {/*MEMBER*/}
                <div className="flex-1 mr-[24px] md:mr-[0px]">
                    {patientDemographicItems.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItem(item, index)
                    })}
                </div>

                {/*SUBSCRIBER*/}
                <div className="flex-1 md:mt-[24px]">
                    {subscriberDemographicItems ? (
                        <>
                            <PrimaryText>Subscriber Information</PrimaryText>

                            <div className="pl-[16px] border-l border-solid border-brand-primary">
                                {subscriberDemographicItems.map(
                                    (item, index) => {
                                        return HtmlHelper.getCoverageBreakdownItem(
                                            item,
                                            index,
                                        )
                                    },
                                )}
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        )
    }, [props.coverageCheckHistory])

    const providerContent = useMemo<JSX.Element | null>(() => {
        const items = props.coverageCheckHistory
            ? CoverageHistoryHelper.getProviderBreakdownItems(
                  props.coverageCheckHistory,
              )
            : []

        // getProviderBreakdownItems can return an empty array
        if (!items.length) {
            return null
        }

        const middleIndex = Math.ceil(items.length / 2)

        const leftData = items.slice(0, middleIndex)
        const rightData = items.slice(middleIndex)

        return (
            <div className="mt-[8px] flex items-start md:block">
                <div className="flex-1 mr-[24px] md:mr-[0px]">
                    {leftData.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItem(item, index)
                    })}
                </div>

                <div className="flex-1">
                    {rightData.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItem(item, index)
                    })}
                </div>
            </div>
        )
    }, [props.coverageCheckHistory])

    const planYearResetContent = useMemo<JSX.Element | null>(() => {
        if (
            !isPlanYearResetsEnabled ||
            !props.coverageCheckHistory?.coverageResult.planResetSoon ||
            !props.coverageCheckHistory.coverageResult.resetBenefitsStatus
        ) {
            return null
        }

        if (
            props.coverageCheckHistory.coverageResult.resetBenefitsStatus ===
            ResetBenefitStatus.NOT_DETECTED
        ) {
            return (
                <PrimaryText className="mt-[12px]">{`${POLICIES_CONFIG.planYearResetsTargetMonth} ${POLICIES_CONFIG.planYearResetsTargetYear} Policy Not Detected`}</PrimaryText>
            )
        }

        const policyDetailItems =
            CoverageHistoryHelper.getBenefitsResetBreakdownItems(
                props.coverageCheckHistory,
                POLICIES_COLUMNS_CONFIG.resetBenefitsPolicyDetailsColumns,
            )

        const thirdPartyPayerItems = props.coverageCheckHistory.coverageResult
            .resetBenefits?.thirdPartyAdministrator
            ? CoverageHistoryHelper.getBenefitsResetBreakdownItems(
                  props.coverageCheckHistory,
                  POLICIES_COLUMNS_CONFIG.resetBenefitsTpaColumns,
              )
            : COVERAGE_CONFIG.defaultResetBenefitsThirdPartyAdministratorItems

        const additionalPolicyItems = props.coverageCheckHistory.coverageResult
            .resetBenefits?.additionalPolicy
            ? CoverageHistoryHelper.getBenefitsResetBreakdownItems(
                  props.coverageCheckHistory,
                  POLICIES_COLUMNS_CONFIG.resetBenefitsAdditionalPolicyColumns,
              )
            : COVERAGE_CONFIG.defaultResetBenefitsAdditionalPolicyItems

        const financialsItems =
            CoverageHistoryHelper.getBenefitsResetBreakdownItems(
                props.coverageCheckHistory,
                POLICIES_COLUMNS_CONFIG.resetBenefitsFinancialsColumns,
            )

        const telehealthFinancialsItems =
            CoverageHistoryHelper.getBenefitsResetBreakdownItems(
                props.coverageCheckHistory,
                POLICIES_COLUMNS_CONFIG.resetBenefitsTelehealthColumns,
            )

        const headerRow = (
            <div className="flex items-center mb-[24px]">
                <div className="w-[225px] mr-[8px]" />
                <div className="flex-1 mr-[8px]">
                    <PrimaryText typography="textSemibold">Current</PrimaryText>
                </div>
                <div className="flex-1">
                    <PrimaryText typography="textSemibold">
                        {`${POLICIES_CONFIG.planYearResetsTargetMonth} ${POLICIES_CONFIG.planYearResetsTargetYear}`}
                    </PrimaryText>
                </div>
            </div>
        )

        return (
            <div className="mt-[12px] flex items-start md:block">
                <div className="flex-1 pb-[24px]">
                    {headerRow}

                    <PrimaryText>Policy Details</PrimaryText>
                    {policyDetailItems.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItemPlanYearReset(
                            item,
                            index,
                        )
                    })}

                    <PrimaryText className="mt-[24px]">
                        Third Party Administrator
                    </PrimaryText>
                    {thirdPartyPayerItems.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItemPlanYearReset(
                            item,
                            index,
                        )
                    })}

                    <PrimaryText className="mt-[24px]">
                        Additional Policy
                    </PrimaryText>
                    {additionalPolicyItems.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItemPlanYearReset(
                            item,
                            index,
                        )
                    })}
                </div>

                <div className="w-[1px] bg-brand-black-transparent-010 self-stretch mx-[24px] block md:hidden" />

                <div className="flex-1 pb-[24px]">
                    {headerRow}

                    <PrimaryText>In Office Financials</PrimaryText>
                    {financialsItems.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItemPlanYearReset(
                            item,
                            index,
                        )
                    })}

                    <PrimaryText className="mt-[24px]">
                        Telehealth Financials
                    </PrimaryText>
                    {telehealthFinancialsItems.map((item, index) => {
                        return HtmlHelper.getCoverageBreakdownItemPlanYearReset(
                            item,
                            index,
                        )
                    })}
                </div>
            </div>
        )
    }, [isPlanYearResetsEnabled, props.coverageCheckHistory])

    return (
        <div
            className={`
                relative
                ${props.className}
            `}
        >
            {!props.hideAgenticCheckStatus &&
            props.coverageCheckHistory?.agenticCheck ? (
                <div className="mb-[24px] flex">
                    <AgenticCheckStatusComponent
                        agenticCheck={props.coverageCheckHistory.agenticCheck}
                    />

                    <div className="flex-1" />
                </div>
            ) : null}

            {props.isSmartScanResult && (
                <PrimaryText
                    className={`
                        mb-[24px] bg-brand-orange010 px-[8px] py-[24px]
                        border border-solid border-brand-orange
                        ${props.rounded ? "rounded-[8px]" : ""}
                    `}
                    typography="textSemibold"
                    centered
                >
                    Please review the data below and ensure the SmartScan
                    results are correct
                </PrimaryText>
            )}

            {props.isInputOverridden && (
                <PrimaryText
                    className={`
                        mb-[24px] bg-brand-orange010 px-[8px] py-[24px]
                        border border-solid border-brand-orange
                        ${props.rounded ? "rounded-[8px]" : ""}
                    `}
                    typography="textSemibold"
                    centered
                >
                    Our results found some differences. Please see below.
                </PrimaryText>
            )}

            <div
                className={`
                    bg-brand-white
                    ${props.rounded ? "rounded-[8px]" : ""}
                `}
            >
                <CollapseElement
                    className="p-[24px]"
                    isDefaultCollapsed={props.isDefaultCollapsed}
                    label="Patient Demographics"
                    content={demographicsContent}
                    rightHeaderContent={
                        <div className="flex items-center flex-wrap mt-[-4px] gap-[8px]">
                            {patientType ? (
                                <CoverageBreakdownBadgeElement
                                    className="my-[4px]"
                                    label={
                                        COVERAGE_CONFIG
                                            .selectRenderedPatientTypes[
                                            patientType
                                        ].displayValue
                                    }
                                />
                            ) : null}

                            {patientId ? (
                                <CoverageBreakdownBadgeElement
                                    className="my-[4px]"
                                    label="Patient ID"
                                    value={patientId}
                                />
                            ) : null}

                            {patientNextAppointmentDate ? (
                                <CoverageBreakdownBadgeElement
                                    className="my-[4px]"
                                    label="Next Appt Date"
                                    value={patientNextAppointmentDate}
                                />
                            ) : null}
                        </div>
                    }
                />

                {providerContent ? (
                    <div>
                        <div className="h-[1px] w-full bg-brand-black-transparent-010" />

                        <CollapseElement
                            className="p-[24px]"
                            label="Provider"
                            isDefaultCollapsed={true}
                            content={providerContent}
                        />
                    </div>
                ) : null}

                {planYearResetContent ? (
                    <div>
                        <div className="h-[1px] w-full bg-brand-black-transparent-010" />

                        <CollapseElement
                            className="p-[24px]"
                            label="Plan Resets Detected"
                            isDefaultCollapsed={false}
                            content={planYearResetContent}
                        />

                        <div className="h-[1px] w-full bg-brand-black-transparent-010" />
                    </div>
                ) : null}
            </div>
        </div>
    )
}

interface ICoverageResultGeneralComponentProps {
    className?: string
    rounded?: boolean
    isDefaultCollapsed?: boolean
    isInputOverridden?: boolean
    isSmartScanResult?: boolean
    hideAgenticCheckStatus?: boolean
    coverageCheckHistory: IOneVerifyCoverageCheckHistory | null
}
